import React, { useCallback, useEffect, useState } from 'react';
import { App, Button, Form, Modal, Table } from 'antd';
import type { Product, ProductQueryParams } from '../../types/product';
import {
  apiProductsBatchPinPOSTAPI,
  apiProductsByIdDELETEAPI,
  apiProductsByIdExternalDetailGETAPI,
  apiProductsByIdPATCHAPI,
  apiProductsByIdPinPATCHAPI,
  apiProductsByIdStatusPATCHAPI,
  apiProductsGETAPI,
  apiProductsPOSTAPI,
  apiProductsSyncexternalPOSTAPI,
} from '../../api/generated/产品管理';
import request from '../../api/request';
import { getPlatform172Detail } from '../../api/external';
import type { Platform172Detail } from '../../api/external';
import ProductForm from './components/ProductForm';
import ProductFilters from './components/ProductFilters';
import ProductDetailDrawer from './components/ProductDetailDrawer';
import ExternalProductDetailDrawer from './components/ExternalProductDetailDrawer';
import { createProductColumns } from './components/productColumns';
import './index.css';

const defaultQueryParams: ProductQueryParams = {
  page: 1,
  pageSize: 10,
};

type RunTaskResponse = {
  queued: boolean;
  taskId: number;
  taskKey?: string;
  runId: number;
  status: string;
  message: string;
};

const ProductsPage: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formVisible, setFormVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pinLoading, setPinLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [proxySyncLoading, setProxySyncLoading] = useState(false);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [currentProduct, setCurrentProduct] = useState<Product | null>(null);
  const [externalDetailVisible, setExternalDetailVisible] = useState(false);
  const [externalDetail, setExternalDetail] = useState<any>(null);
  const [externalDetailLoading, setExternalDetailLoading] = useState(false);
  const [platform172Detail, setPlatform172Detail] = useState<Platform172Detail | null>(null);
  const [queryParams, setQueryParams] = useState<ProductQueryParams>(defaultQueryParams);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiProductsGETAPI({
        page: queryParams.page,
        pageSize: queryParams.pageSize,
        keyword: queryParams.keyword,
        operator: queryParams.operator,
        status: queryParams.status,
        external_status: queryParams.external_status,
        source_platform: queryParams.source_platform,
        is_hidden_from_client: queryParams.is_hidden_from_client,
        duplicate_status: queryParams.duplicate_status,
      });

      const items = response.items || [];
      setProducts(items as Product[]);
      setTotal(response.total || items.length);
      setCurrent(response.current || queryParams.page || 1);
      setPageSize(response.pageSize || queryParams.pageSize || 10);
    } catch (error: any) {
      console.error('获取产品列表错误:', error);
      setProducts([]);
      setTotal(0);
      messageApi.error(error.response?.data?.message || '获取产品列表失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [queryParams, messageApi]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const closeProductForm = () => {
    setFormVisible(false);
    setEditingProduct(null);
    form.resetFields();
  };

  const openCreateProduct = () => {
    setEditingProduct(null);
    form.resetFields();
    setFormVisible(true);
  };

  const showProductDetail = (product: Product) => {
    setCurrentProduct(product);
    setDetailDrawerVisible(true);
  };

  const closeProductDetail = () => {
    setDetailDrawerVisible(false);
    setPlatform172Detail(null);
  };

  const handleTogglePin = async (id: number, isPinned: boolean) => {
    try {
      await apiProductsByIdPinPATCHAPI({ id }, { is_pinned: !isPinned });
      messageApi.success(`${!isPinned ? '置顶' : '取消置顶'}成功`);
      fetchProducts();
    } catch (error: any) {
      messageApi.error(error.response?.data?.message || `${!isPinned ? '置顶' : '取消置顶'}失败，请重试`);
    }
  };

  const handleBatchPin = async (isPinned: boolean) => {
    if (selectedRowKeys.length === 0) {
      messageApi.warning('请先选择产品');
      return;
    }

    try {
      setPinLoading(true);
      await apiProductsBatchPinPOSTAPI({
        ids: selectedRowKeys.map((key) => Number(key)),
        is_pinned: isPinned,
      });
      messageApi.success(`批量${isPinned ? '置顶' : '取消置顶'}成功`);
      setSelectedRowKeys([]);
      fetchProducts();
    } catch (error: any) {
      messageApi.error(error.response?.data?.message || `批量${isPinned ? '置顶' : '取消置顶'}失败，请重试`);
    } finally {
      setPinLoading(false);
    }
  };

  const handleSyncExternal = async () => {
    try {
      setSyncLoading(true);
      messageApi.loading({ content: '正在同步外部产品...', key: 'syncLoading', duration: 0 });
      const response = await apiProductsSyncexternalPOSTAPI();
      const appSupplement = response.appSupplement;

      messageApi.success({
        content: (
          <div>
            <p>同步成功！共处理 {response.totalSynced || response.count || 0} 个产品:</p>
            <ul>
              <li>新增: {response.created || 0} 个</li>
              <li>更新: {response.updated || 0} 个</li>
              <li>标记下架: {response.marked || 0} 个</li>
              {appSupplement && (
                <li>
                  APP补充: 更新 {appSupplement.updatedProducts || 0} / {appSupplement.totalProducts || 0} 个
                </li>
              )}
            </ul>
            {response.errors && response.errors.length > 0 && (
              <div style={{ color: '#fa8c16' }}>
                <p>发生 {response.errors.length} 个错误:</p>
                <ul>
                  {response.errors.slice(0, 3).map((err: string, index: number) => (
                    <li key={index}>{err}</li>
                  ))}
                  {response.errors.length > 3 && <li>... 还有 {response.errors.length - 3} 个错误</li>}
                </ul>
              </div>
            )}
          </div>
        ),
        key: 'syncLoading',
        duration: 5,
      });
      fetchProducts();
    } catch (error: any) {
      messageApi.error({
        content: `同步失败: ${error.message || '未知错误'}`,
        key: 'syncLoading',
      });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSyncExternalProxy = () => {
    Modal.confirm({
      title: '启动代理同步产品？',
      content: '代理同步会在服务器后台运行，通常需要几十分钟。启动后可以在“同步任务管理”里查看进度和执行记录。',
      okText: '启动代理同步',
      cancelText: '取消',
      onOk: async () => {
        try {
          setProxySyncLoading(true);
          const result = await request.post<any, RunTaskResponse>('/api/sync-tasks/key/product-external-proxy/run');
          messageApi.success(
            `代理同步已启动，运行记录 #${result.runId}。可以到同步任务管理查看进度。`,
          );
        } catch (error: any) {
          messageApi.error(error?.response?.data?.message || '启动代理同步失败');
        } finally {
          setProxySyncLoading(false);
        }
      },
    });
  };

  const fetchPlatform172Detail = async (product: Product) => {
    if (!product.source_product_id) {
      messageApi.warning('该产品没有172平台ID');
      return;
    }

    try {
      const detail = await getPlatform172Detail(
        product.source_product_id,
        product.source_data_snapshot?.api?.userSn,
      );
      setCurrentProduct(product);
      setPlatform172Detail(detail);
      setDetailDrawerVisible(true);
    } catch (error: any) {
      messageApi.error(error?.response?.data?.message || '获取172详情失败');
    }
  };

  const fetchExternalDetail = async (id: number) => {
    try {
      setExternalDetailLoading(true);
      const data = await apiProductsByIdExternalDetailGETAPI({ id });
      setExternalDetail(data);
      setExternalDetailVisible(true);
    } catch (error: any) {
      messageApi.error(error.response?.data?.message || '获取外部产品详情失败');
    } finally {
      setExternalDetailLoading(false);
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    form.setFieldsValue(product);
    setFormVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiProductsByIdDELETEAPI({ id });
      messageApi.success('产品删除成功');
      if (products.length === 1 && current > 1) {
        const previousPage = current - 1;
        setCurrent(previousPage);
        setQueryParams({
          ...queryParams,
          page: previousPage,
        });
      } else {
        fetchProducts();
      }
    } catch (error: any) {
      if (error.response) {
        messageApi.error(error.response.data.message || '删除失败，请重试');
      } else {
        messageApi.error('删除失败，请检查网络连接');
      }
    }
  };

  const handleToggleStatus = async (id: number, status: '在售' | '已下架') => {
    try {
      await apiProductsByIdStatusPATCHAPI({ id }, { status });
      messageApi.success('产品状态更新成功');
      fetchProducts();
    } catch (error: any) {
      messageApi.error(error.response?.data?.message || '状态更新失败，请重试');
      fetchProducts();
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingProduct) {
        const updateData = { ...values };
        delete updateData.id;
        await apiProductsByIdPATCHAPI({ id: editingProduct.id }, updateData);
        messageApi.success('产品更新成功');
      } else {
        await apiProductsPOSTAPI(values);
        messageApi.success('产品创建成功');
      }
      closeProductForm();
      fetchProducts();
    } catch (error: any) {
      messageApi.error(error.response?.data?.message || '操作失败，请重试');
    }
  };

  const handleSearch = (values: ProductQueryParams) => {
    setQueryParams({
      ...queryParams,
      ...values,
      page: 1,
    });
  };

  const handleReset = () => {
    searchForm.resetFields();
    setQueryParams(defaultQueryParams);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  const columns = createProductColumns({
    onEdit: handleEdit,
    onDelete: handleDelete,
    onTogglePin: handleTogglePin,
    onToggleStatus: handleToggleStatus,
    onShowDetail: showProductDetail,
    onFetchPlatform172Detail: fetchPlatform172Detail,
    onFetchExternalDetail: fetchExternalDetail,
  });

  return (
    <div className="products-page" style={{ padding: 0, margin: 0, height: '100%' }}>
      <ProductFilters
        searchForm={searchForm}
        queryParams={queryParams}
        selectedCount={selectedRowKeys.length}
        pinLoading={pinLoading}
        syncLoading={syncLoading}
        proxySyncLoading={proxySyncLoading}
        onSearch={handleSearch}
        onReset={handleReset}
        onCreate={openCreateProduct}
        onSyncExternal={handleSyncExternal}
        onSyncExternalProxy={handleSyncExternalProxy}
        onBatchPin={handleBatchPin}
      />

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={products}
        rowKey="id"
        loading={loading}
        scroll={{ x: 'max-content', y: 'calc(100vh - 330px)' }}
        pagination={{
          current,
          pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (count) => `共 ${count} 条`,
          onChange: (page, size) => {
            setCurrent(page);
            setPageSize(size || 10);
            setQueryParams({
              ...queryParams,
              page,
              pageSize: size || 10,
            });
          },
          style: { marginTop: 8 },
        }}
        size="middle"
        bordered={false}
      />

      <Modal
        title={editingProduct ? '编辑产品' : '新建产品'}
        open={formVisible}
        onCancel={closeProductForm}
        footer={[
          <Button key="cancel" onClick={closeProductForm}>
            取消
          </Button>,
          <Button key="submit" type="primary" form="productForm" htmlType="submit">
            保存
          </Button>,
        ]}
        width={800}
      >
        <ProductForm
          form={form}
          initialValues={editingProduct}
          onSubmit={handleSubmit}
          onCancel={closeProductForm}
        />
      </Modal>

      <ProductDetailDrawer
        open={detailDrawerVisible}
        product={currentProduct}
        platform172Detail={platform172Detail}
        onClose={closeProductDetail}
      />

      <ExternalProductDetailDrawer
        open={externalDetailVisible}
        loading={externalDetailLoading}
        detail={externalDetail}
        onClose={() => setExternalDetailVisible(false)}
      />
    </div>
  );
};

export default ProductsPage;
